import { GameMode, GameModeStatus, GameHazard } from './GameMode';

// Damage scaling difficulty levels for Fight mode
export type DamageScaling = 'normal' | 'hard' | 'extreme';

// Game mode display names
export const GAME_MODE_NAMES: Record<GameMode, string> = {
    [GameMode.CLASSIC]: "Classic",
    [GameMode.TIMED]: "Timed",
    [GameMode.FIGHT]: "Fight",
    [GameMode.ENDLESS]: "Endless",
    [GameMode.DAILY]: "Daily",
    [GameMode.FLASH]: "Flash",
    [GameMode.HAZARD]: "Hazard",
    [GameMode.COOP]: "Coop",
    [GameMode.CUSTOM]: "Custom"
};

// Base interface for all advanced settings
export interface BaseAdvancedSettings {
    gameMode: GameMode;
    maxRounds: number;
    timerSeconds: number | null; // null means no timer
    hasSpecialRules: boolean;
    status: GameModeStatus;
    minPlayers: number;
    maxPlayers: number;
}

// Classic mode settings - only rounds, no timer
export interface ClassicModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.CLASSIC;
    maxRounds: number;
    timerSeconds: null;
}

// Timed mode settings - rounds and timer
export interface TimedModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.TIMED;
    maxRounds: number;
    timerSeconds: number;
}

// Fight mode settings - team-based, unlimited rounds until one team survives
export interface FightModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.FIGHT;
    maxRounds: -1; // Unlimited rounds until victory
    timerSeconds: number;
    teamMaxHp: number; // Max HP per team (default 10000)
    numberOfTeams: number; // Number of teams (2-4)
    damageMultiplierScaling: DamageScaling; // Damage scaling difficulty (default 'normal')
}

// Endless mode settings - unlimited rounds with timer, streak-based
export interface EndlessModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.ENDLESS;
    maxRounds: -1; // Unlimited
    timerSeconds: null; // Timer for each round
}

// Daily mode settings - fixed seed
export interface DailyModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.DAILY;
    maxRounds: number;
    timerSeconds: number;
    dailySeed: string;
}

// Flash mode settings - quick rounds with limited view time
export interface FlashModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.FLASH;
    maxRounds: number;
    timerSeconds: null; // No timer in Flash mode
    flashDuration: number; // Duration in milliseconds for photosphere visibility
}

// Hazard mode settings - visual effects and obstacles with timer and multiple hazards per round
export interface HazardModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.HAZARD;
    maxRounds: number;
    timerSeconds: number; // Timer in seconds
    hazardsPerRound: number; // Number of hazards applied per round (default: 1)
    enabledHazards?: string[]; // Optional array of enabled hazard IDs (if not provided, all are enabled)
}

// Coop mode settings - collaborative team gameplay where one player sees photosphere and the other sees map
export interface CoopModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.COOP;
    maxRounds: number;
    timerSeconds: number;
    numberOfTeams: number; // Number of teams (1-4), each team must have exactly 2 players
}

// Custom mode base types
export type CustomBaseMode = GameMode.CLASSIC | GameMode.TIMED | GameMode.FIGHT | GameMode.ENDLESS | GameMode.COOP;

// Custom mode settings - fully customizable
export interface CustomModeSettings extends BaseAdvancedSettings {
    gameMode: GameMode.CUSTOM;
    baseMode: CustomBaseMode; // The base mode to use
    maxRounds: number; // Can be -1 for unlimited
    timerSeconds: number | null; // Can be null for no timer

    // Fight mode properties (when baseMode is FIGHT)
    teamMaxHp?: number;
    numberOfTeams?: number;
    damageMultiplierScaling?: DamageScaling;

    // Coop mode properties (when baseMode is COOP)
    // numberOfTeams is shared with Fight mode

    // Flash mode toggle and settings
    hasFlashMode: boolean;
    flashDuration?: number; // Duration in milliseconds

    // Hazard mode toggle and settings
    hasHazards: boolean;
    hazardsPerRound?: number;
    enabledHazards?: string[];
    
    // Compass toggle
    compassDisabled: boolean;
}

// Union type for all advanced settings
export type GameModeAdvancedSettings =
    | ClassicModeSettings
    | TimedModeSettings
    | FightModeSettings
    | EndlessModeSettings
    | DailyModeSettings
    | FlashModeSettings
    | HazardModeSettings
    | CoopModeSettings
    | CustomModeSettings;

// Default configurations for all game modes
export const DEFAULT_GAME_MODE_SETTINGS: Record<GameMode, GameModeAdvancedSettings> = {
        [GameMode.DAILY]: {
        gameMode: GameMode.DAILY,
        maxRounds: 5,
        timerSeconds: 60,
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 1, // Daily mode is solo only
        dailySeed: ""
    },
    [GameMode.CLASSIC]: {
        gameMode: GameMode.CLASSIC,
        maxRounds: 5,
        timerSeconds: null, // No timer
        hasSpecialRules: false,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8
    },
    [GameMode.TIMED]: {
        gameMode: GameMode.TIMED,
        maxRounds: 5,
        timerSeconds: 60, // 1-minute timer
        hasSpecialRules: false,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8
    },
    [GameMode.HAZARD]: {
        gameMode: GameMode.HAZARD,
        maxRounds: 5,
        timerSeconds: 60, // Default 60 seconds timer
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8,
        hazardsPerRound: 1, // Default to 1 hazard per round
        enabledHazards: Object.values(GameHazard) // All hazards enabled by default
    },
    [GameMode.COOP]: {
        gameMode: GameMode.COOP,
        maxRounds: 5,
        timerSeconds: 60,
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 2,
        maxPlayers: 8,
        numberOfTeams: 1 // Default to 1 team (2 players)
    },
    [GameMode.FIGHT]: {
        gameMode: GameMode.FIGHT,
        maxRounds: -1, // Unlimited rounds until victory
        timerSeconds: 60,
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 2,
        maxPlayers: 8,
        teamMaxHp: 10000,
        numberOfTeams: 2,
        damageMultiplierScaling: 'normal'
    },
    [GameMode.ENDLESS]: {
        gameMode: GameMode.ENDLESS,
        maxRounds: -1, // Unlimited
        timerSeconds: null,
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8
    },
    [GameMode.FLASH]: {
        gameMode: GameMode.FLASH,
        maxRounds: 5,
        timerSeconds: null, // No timer in Flash mode
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8,
        flashDuration: 1000 // 1000ms (1 second) to view photosphere
    },
    [GameMode.CUSTOM]: {
        gameMode: GameMode.CUSTOM,
        baseMode: GameMode.TIMED,
        maxRounds: 5,
        timerSeconds: 60,
        hasSpecialRules: true,
        status: GameModeStatus.AVAILABLE,
        minPlayers: 1,
        maxPlayers: 8,
        // Fight mode defaults
        teamMaxHp: 10000,
        numberOfTeams: 2,
        damageMultiplierScaling: 'normal',
        // Flash mode defaults
        hasFlashMode: false,
        flashDuration: 1000,
        // Hazard mode defaults
        hasHazards: false,
        hazardsPerRound: 1,
        enabledHazards: Object.values(GameHazard),
        // Compass defaults
        compassDisabled: false
    }
};

// Helper function to get default settings for a game mode
export function getDefaultAdvancedSettings(gameMode: GameMode): GameModeAdvancedSettings {
    return DEFAULT_GAME_MODE_SETTINGS[gameMode];
}

// Helper function to get customizable settings for a game mode (for user UI)
export function getCustomizableAdvancedSettings(gameMode: GameMode): Partial<GameModeAdvancedSettings> | null {
    switch (gameMode) {
        case GameMode.CLASSIC:
            return {
                maxRounds: 5
            };
        case GameMode.TIMED:
            return {
                maxRounds: 5,
                timerSeconds: 60
            };
        case GameMode.FLASH:
            return {
                maxRounds: 5,
                flashDuration: 1000
            };
        case GameMode.ENDLESS:
            return {};
        case GameMode.HAZARD:
            return {
                maxRounds: 5,
                timerSeconds: 60,
                hazardsPerRound: 1
            };
        case GameMode.FIGHT:
            return {
                timerSeconds: 60,
                teamMaxHp: 10000,
                numberOfTeams: 2,
                damageMultiplierScaling: 'normal'
            };
        case GameMode.COOP:
            return {
                maxRounds: 5,
                timerSeconds: 60,
                numberOfTeams: 1
            };
        case GameMode.DAILY:
            return null; // Daily mode has no customizable settings
        case GameMode.CUSTOM:
            return {
                baseMode: GameMode.TIMED,
                maxRounds: 5,
                timerSeconds: 60,
                hasFlashMode: false,
                flashDuration: 1000,
                hasHazards: false,
                hazardsPerRound: 1
            };
        default:
            return null; // Other game modes don't have customizable settings yet
    }
}

// Type guard functions
export function isClassicModeSettings(settings: GameModeAdvancedSettings): settings is ClassicModeSettings {
    return settings.gameMode === GameMode.CLASSIC;
}

export function isTimedModeSettings(settings: GameModeAdvancedSettings): settings is TimedModeSettings {
    return settings.gameMode === GameMode.TIMED;
}

export function isFightModeSettings(settings: GameModeAdvancedSettings): settings is FightModeSettings {
    return settings.gameMode === GameMode.FIGHT;
}

export function isEndlessModeSettings(settings: GameModeAdvancedSettings): settings is EndlessModeSettings {
    return settings.gameMode === GameMode.ENDLESS;
}

export function isDailyModeSettings(settings: GameModeAdvancedSettings): settings is DailyModeSettings {
    return settings.gameMode === GameMode.DAILY;
}

export function isFlashModeSettings(settings: GameModeAdvancedSettings): settings is FlashModeSettings {
    return settings.gameMode === GameMode.FLASH;
}

export function isHazardModeSettings(settings: GameModeAdvancedSettings): settings is HazardModeSettings {
    return settings.gameMode === GameMode.HAZARD;
}

export function isCoopModeSettings(settings: GameModeAdvancedSettings): settings is CoopModeSettings {
    return settings.gameMode === GameMode.COOP;
}

export function isCustomModeSettings(settings: GameModeAdvancedSettings): settings is CustomModeSettings {
    return settings.gameMode === GameMode.CUSTOM;
}

// Runtime configuration storage for dynamic updates
let runtimeGameModeSettings: Record<GameMode, GameModeAdvancedSettings> = { ...DEFAULT_GAME_MODE_SETTINGS };

// Function to update runtime settings (for real-time updates)
export function updateGameModeSettings(mode: GameMode, settings: Partial<GameModeAdvancedSettings>): void {
    runtimeGameModeSettings[mode] = {
        ...runtimeGameModeSettings[mode],
        ...settings
    } as GameModeAdvancedSettings;
}

// Function to reset runtime settings to defaults
export function resetGameModeSettings(): void {
    runtimeGameModeSettings = { ...DEFAULT_GAME_MODE_SETTINGS };
}

// Function to get current runtime settings for a mode
export function getRuntimeGameModeSettings(mode: GameMode): GameModeAdvancedSettings {
    return runtimeGameModeSettings[mode];
}

// Helper functions for working with configurations (now using runtime settings)
export function getAvailableGameModes(useRuntimeSettings: boolean = false): GameModeAdvancedSettings[] {
    const settings = useRuntimeSettings ? runtimeGameModeSettings : DEFAULT_GAME_MODE_SETTINGS;
    return Object.values(settings).filter(
        mode => mode.status === GameModeStatus.AVAILABLE
    );
}

export function getComingSoonGameModes(useRuntimeSettings: boolean = false): GameModeAdvancedSettings[] {
    const settings = useRuntimeSettings ? runtimeGameModeSettings : DEFAULT_GAME_MODE_SETTINGS;
    return Object.values(settings).filter(
        mode => mode.status === GameModeStatus.COMING_SOON
    );
}

export function getAllGameModes(useRuntimeSettings: boolean = false): GameModeAdvancedSettings[] {
    const settings = useRuntimeSettings ? runtimeGameModeSettings : DEFAULT_GAME_MODE_SETTINGS;
    return Object.values(settings);
}

export function getGameModeByType(mode: GameMode, useRuntimeSettings: boolean = false): GameModeAdvancedSettings {
    const settings = useRuntimeSettings ? runtimeGameModeSettings : DEFAULT_GAME_MODE_SETTINGS;
    return settings[mode];
}

export function getGameModeName(mode: GameMode): string {
    return GAME_MODE_NAMES[mode];
}

// Function to create a customized settings object based on user input
export function createCustomizedSettings(
    baseMode: GameMode,
    customizations: Partial<GameModeAdvancedSettings>
): GameModeAdvancedSettings {
    const defaultSettings = DEFAULT_GAME_MODE_SETTINGS[baseMode];
    return {
        ...defaultSettings,
        ...customizations
    } as GameModeAdvancedSettings;
}